import { createApiClient, type ApiClient } from '@luacentrix/api-client';
import { isApiError } from '@luacentrix/shared';

export const SESSION_TOKEN_STORAGE_KEY = 'luacentrix.session_token';

export interface DashboardEnv {
  apiBaseUrl: string;
  requireHttps: boolean;
}

export interface ErrorPresentation {
  code: string;
  message: string;
  status: number | null;
  field: string | null;
}

function firstString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function resolveEnv(source: Record<string, unknown> = {}): DashboardEnv {
  const apiBaseUrl = firstString(source['VITE_API_BASE_URL']) ?? window.location.origin;
  return {
    apiBaseUrl,
    requireHttps:
      !apiBaseUrl.startsWith('http://localhost') && !apiBaseUrl.startsWith('http://127.0.0.1'),
  };
}

function storedSessionToken(): string | null {
  try {
    return window.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function createApi(env: DashboardEnv = resolveEnv()): ApiClient {
  return createApiClient({
    baseUrl: env.apiBaseUrl,
    accessToken: () => storedSessionToken(),
    credentials: 'include',
    requireHttps: env.requireHttps,
    timeoutMs: 15_000,
  });
}

export function describeError(reason: unknown): ErrorPresentation {
  if (isApiError(reason)) {
    return {
      code: reason.code,
      message: reason.message,
      status: reason.status,
      field: firstString(reason.details?.field) ?? null,
    };
  }
  if (reason instanceof Error) {
    return { code: 'UNKNOWN_ERROR', message: reason.message, status: null, field: null };
  }
  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred.',
    status: null,
    field: null,
  };
}
